import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Table, Button, Row, Col, Form, InputGroup, Pagination,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  FaTrashAlt, FaSort, FaSortUp, FaSortDown, FaSearch,
} from 'react-icons/fa';

import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { selectStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  buscarDescontosVendaUsuario,
  buscarUsuariosDescontoVenda,
  buscarFamiliasDescontoVenda,
  incluirDescontoVendaUsuario,
  excluirDescontoVendaUsuario,
} from '~/Services/Service';

const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
const REGISTROS_POR_PAGINA = 10;

const TabDescontosUsuario = () => {
  const [descontos, setDescontos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtro, setFiltro] = useState('');
  const [filtroPor, setFiltroPor] = useState('usuario');
  const [filtroCarencia, setFiltroCarencia] = useState('');
  const [filtroPctMin, setFiltroPctMin] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);

  const [ordenacao, setOrdenacao] = useState({ campo: 'nome_usuario', direcao: 'asc' });

  const [usuarioSel, setUsuarioSel] = useState(null);
  const [familiaSel, setFamiliaSel] = useState(null);
  const [carencia, setCarencia] = useState(false);
  const [percentual, setPercentual] = useState('');
  const [incluindo, setIncluindo] = useState(false);

  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const [descontoExcluindo, setDescontoExcluindo] = useState(null);

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    setLoading(true);
    try {
      const [resDescontos, resUsuarios, resFamilias] = await Promise.all([
        buscarDescontosVendaUsuario(),
        buscarUsuariosDescontoVenda(),
        buscarFamiliasDescontoVenda(),
      ]);
      setDescontos(resDescontos || []);
      setUsuarios(resUsuarios || []);
      setFamilias(resFamilias || []);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const carregarDescontos = async () => {
    try {
      const res = await buscarDescontosVendaUsuario();
      setDescontos(res || []);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar descontos');
    }
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const handleIncluir = async () => {
    if (!usuarioSel) {
      toast.error('Necessário informar um usuário');
      return;
    }
    const pct = parseFloat(String(percentual).replace(',', '.'));
    if (percentual !== '' && pct < 0) {
      toast.error('Necessário informar percentual de desconto positivo');
      return;
    }
    setIncluindo(true);
    try {
      await incluirDescontoVendaUsuario({
        codigo_usuario: usuarioSel.value,
        codigo_familia: familiaSel ? familiaSel.value : null,
        carencia: carencia ? 'S' : 'N',
        percentual: pct || 0,
      });
      toast.success('Desconto incluído com sucesso!');
      setUsuarioSel(null);
      setFamiliaSel(null);
      setCarencia(false);
      setPercentual('');
      carregarDescontos();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao incluir desconto');
    } finally {
      setIncluindo(false);
    }
  };

  const handleConfirmarExclusao = (desconto) => {
    setDescontoExcluindo(desconto);
    setShowConfirmacao(true);
  };

  const handleExcluir = async (confirmado) => {
    setShowConfirmacao(false);
    if (!confirmado) {
      setDescontoExcluindo(null);
      return;
    }
    try {
      await excluirDescontoVendaUsuario({
        codigo_usuario: descontoExcluindo.codigo_usuario,
        codigo_familia: descontoExcluindo.codigo_familia,
      });
      toast.success('Desconto excluído com sucesso!');
      carregarDescontos();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao excluir desconto');
    } finally {
      setDescontoExcluindo(null);
    }
  };

  const handleLimparFiltros = () => {
    setFiltro('');
    setFiltroCarencia('');
    setFiltroPctMin('');
  };

  const opcoesUsuarios = usuarios.map((u) => ({
    value: u.codigo_usuario,
    label: u.nome_usuario,
  }));

  const opcoesFamilias = familias.map((f) => ({
    value: f.codigo_familia,
    label: f.nome_da_familia,
  }));

  const descontosFiltrados = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    const min = filtroPctMin === '' ? null : parseFloat(String(filtroPctMin).replace(',', '.'));
    return descontos.filter((d) => {
      if (termo) {
        const campoBusca = filtroPor === 'usuario'
          ? (d.nome_usuario || '')
          : (d.nome_da_familia || '');
        if (campoBusca.toLowerCase().indexOf(termo) < 0) return false;
      }
      if (filtroCarencia && d.carencia !== filtroCarencia) return false;
      if (min !== null && !Number.isNaN(min) && (Number(d.percentual) || 0) < min) return false;
      return true;
    });
  }, [descontos, filtro, filtroPor, filtroCarencia, filtroPctMin]);

  const descontosOrdenados = useMemo(() => {
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const campo = ordenacao.campo;
    return [...descontosFiltrados].sort((a, b) => {
      const valA = a[campo];
      const valB = b[campo];
      if (campo === 'percentual') {
        return dir * ((valA || 0) - (valB || 0));
      }
      return dir * String(valA || '').toLowerCase().localeCompare(String(valB || '').toLowerCase());
    });
  }, [descontosFiltrados, ordenacao]);

  const totalPaginas = Math.max(1, Math.ceil(descontosOrdenados.length / REGISTROS_POR_PAGINA));

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro, filtroPor, filtroCarencia, filtroPctMin, descontos.length]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  const descontosPaginados = useMemo(() => {
    const fim = paginaAtual * REGISTROS_POR_PAGINA;
    const inicio = fim - REGISTROS_POR_PAGINA;
    return descontosOrdenados.slice(inicio, fim);
  }, [descontosOrdenados, paginaAtual]);

  if (loading) return <Loading />;

  return (
    <>
      {/* Formulário inline de inclusão */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="py-3">
          <Row className="align-items-end">
            <Col md={3} sm={6} className="mb-2">
              <Form.Label className="small mb-1">Usuário</Form.Label>
              <Select
                value={usuarioSel}
                onChange={setUsuarioSel}
                options={opcoesUsuarios}
                placeholder="Selecione..."
                styles={selectStylesSm}
                components={{ DropdownIndicator }}
                isClearable
              />
            </Col>
            <Col md={3} sm={6} className="mb-2">
              <Form.Label className="small mb-1">Família (vazio = avulso)</Form.Label>
              <Select
                value={familiaSel}
                onChange={setFamiliaSel}
                options={opcoesFamilias}
                placeholder="Selecione..."
                styles={selectStylesSm}
                components={{ DropdownIndicator }}
                isClearable
              />
            </Col>
            <Col md={3} sm={6} className="mb-2">
              <Form.Label className="small mb-1">% Desconto</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                step="0.01"
                value={percentual}
                onChange={(e) => setPercentual(e.target.value)}
                placeholder="0.00"
              />
            </Col>
            <Col md={2} sm={6} className="mb-2 d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label={<span style={{ fontSize: '0.85rem' }}>Libera Qtd. dias de carência para 1ª parcela</span>}
                checked={carencia}
                onChange={(e) => setCarencia(e.target.checked)}
              />
            </Col>
            <Col md={1} sm={12} className="mb-2 text-right">
              <Button
                size="sm"
                variant="success"
                onClick={handleIncluir}
                disabled={incluindo}
              >
                {incluindo ? '...' : 'Incluir'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <hr />

      {/* Filtros */}
      <Row className="mb-2 align-items-end">
        <Col md={4} sm={12} className="mb-2">
          <Form.Label className="small mb-1">Buscar por nome</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Prepend>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
              type="text"
              placeholder="Digite parte do nome..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3} sm={6} className="mb-2 d-flex align-items-center" style={{ gap: 12, paddingTop: 24 }}>
          <Form.Check
            inline
            type="radio"
            label="Usuário"
            value="usuario"
            checked={filtroPor === 'usuario'}
            onChange={() => setFiltroPor('usuario')}
            id="filtroPorUsuario"
          />
          <Form.Check
            inline
            type="radio"
            label="Família"
            value="familia"
            checked={filtroPor === 'familia'}
            onChange={() => setFiltroPor('familia')}
            id="filtroPorFamilia"
          />
        </Col>
        <Col md={2} sm={6} className="mb-2">
          <Form.Label className="small mb-1">Carência</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={filtroCarencia}
            onChange={(e) => setFiltroCarencia(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="S">Sim</option>
            <option value="N">Não</option>
          </Form.Control>
        </Col>
        <Col md={2} sm={6} className="mb-2">
          <Form.Label className="small mb-1">% Desconto mín.</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            step="0.01"
            placeholder="0"
            value={filtroPctMin}
            onChange={(e) => setFiltroPctMin(e.target.value)}
          />
        </Col>
        <Col md={1} sm={12} className="mb-2 text-right">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleLimparFiltros}
            title="Limpar filtros"
          >
            Limpar
          </Button>
        </Col>
      </Row>

      {/* Grid */}
      <Card className="shadow-sm">
        <Card.Header className="py-2 bg-light">
          <span className="text-muted small">
            Descontos por Usuário — {descontosOrdenados.length}{' '}
            {descontosOrdenados.length === 1 ? 'registro' : 'registros'}
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          {descontosOrdenados.length === 0 ? (
            <p className="text-muted text-center my-4">
              {descontos.length === 0
                ? 'Nenhum desconto cadastrado.'
                : 'Nenhum resultado para os filtros aplicados.'}
            </p>
          ) : (
            <>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th style={thSortStyle} onClick={() => handleOrdenacao('nome_usuario')}>
                      Usuário{iconeOrdenacao('nome_usuario')}
                    </th>
                    <th style={thSortStyle} onClick={() => handleOrdenacao('nome_da_familia')}>
                      Família{iconeOrdenacao('nome_da_familia')}
                    </th>
                    <th style={{ ...thSortStyle, width: '100px', textAlign: 'center' }} onClick={() => handleOrdenacao('carencia')}>
                      Carência{iconeOrdenacao('carencia')}
                    </th>
                    <th style={{ ...thSortStyle, width: '110px', textAlign: 'right' }} onClick={() => handleOrdenacao('percentual')}>
                      % Desconto{iconeOrdenacao('percentual')}
                    </th>
                    <th style={{ width: '70px', textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {descontosPaginados.map((d) => (
                    <tr key={`${d.codigo_usuario}-${d.codigo_familia}`}>
                      <td>{d.nome_usuario}</td>
                      <td>{d.nome_da_familia}</td>
                      <td className="text-center">
                        {d.carencia === 'S' ? 'Sim' : 'Não'}
                      </td>
                      <td className="text-right">
                        {Number(d.percentual).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="danger"
                          title="Excluir"
                          onClick={() => handleConfirmarExclusao(d)}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div
                className="d-flex justify-content-start align-items-center flex-wrap px-3 py-2"
                style={{ borderTop: '1px solid #dee2e6', gap: '12px' }}
              >
                <Pagination size="sm" className="mb-0">
                  <Pagination.First
                    onClick={() => setPaginaAtual(1)}
                    disabled={paginaAtual === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setPaginaAtual(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                  />
                  {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                    <Pagination.Item
                      key={n}
                      active={paginaAtual === n}
                      onClick={() => setPaginaAtual(n)}
                    >
                      {n}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => setPaginaAtual(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                  />
                  <Pagination.Last
                    onClick={() => setPaginaAtual(totalPaginas)}
                    disabled={paginaAtual === totalPaginas}
                  />
                </Pagination>
                <small className="text-muted">
                  {`Mostrando ${(paginaAtual - 1) * REGISTROS_POR_PAGINA + 1} até ${Math.min(paginaAtual * REGISTROS_POR_PAGINA, descontosOrdenados.length)} de ${descontosOrdenados.length} registros`}
                </small>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={showConfirmacao}
        title="Excluir Desconto"
        message={
          descontoExcluindo
            ? `Deseja excluir o desconto do usuário "${descontoExcluindo.nome_usuario}" / família "${descontoExcluindo.nome_da_familia}"?`
            : ''
        }
        onClose={handleExcluir}
      />
    </>
  );
};

export default TabDescontosUsuario;
